import { graphql, PageProps } from "gatsby"
import { motion, Variants } from "framer-motion"
import cc from "classcat"

import SEO from "../components/seo"
import useTranslations from "../hooks/useTranslations"
import { formatArticles } from "../utils/prismic"
import ScrollAnimate from "../components/atoms/common/ScrollAnimate"
import EmptyState from "../components/atoms/common/EmptyState"

type Article = {
  id: string
  content: string
  publishedAt: string
  url: string
  logo: string
  publisherName: string
}

type MediaCardProps = Article & {
  readArticleText: string
}

type HeroSectionProps = {
  title: string
  description: string
}

type MediaPageData = {
  allPrismicNewsArticle: {
    edges: Array<{
      node: {
        id: string
        data: {
          article: { text: string }
          published_at: string
          link_to_article: { url: string }
          publisher_logo: { url: string }
          publisher_name: { text: string }
        }
      }
    }>
  }
}

type MediaPageContext = {
  wellKnownMedia?: string[]
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
}

const MediaCard = ({
  publisherName,
  publishedAt,
  url,
  content,
  logo,
  readArticleText,
}: MediaCardProps) => (
  <motion.a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="group block h-full"
    variants={itemVariants}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <div
      className={cc([
        "relative h-full overflow-hidden rounded-2xl border border-zinc-200 bg-white p-6",
        "shadow-sm transition-all duration-300",
        "group-hover:border-kkm-green/50 group-hover:shadow-lg group-hover:shadow-kkm-green/10",
      ])}
    >
      {/* Gradient overlay on hover */}
      <div
        className={cc([
          "pointer-events-none absolute inset-0 bg-gradient-to-br from-kkm-green/5 to-transparent",
          "opacity-0 transition-opacity duration-300 group-hover:opacity-100",
        ])}
      />

      <div className="relative flex h-full flex-col">
        {/* Header with logo and meta */}
        <div className="mb-4 flex items-center gap-4">
          <div
            className={cc([
              "flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-zinc-50 p-2",
              "ring-1 ring-zinc-100 transition-all duration-300",
              "group-hover:ring-2 group-hover:ring-kkm-green/30",
            ])}
          >
            <img
              src={logo}
              alt={publisherName}
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-zinc-900 transition-colors group-hover:text-kkm-darkgreen">
              {publisherName}
            </span>
            <span className="text-xs text-zinc-500">{publishedAt}</span>
          </div>
        </div>

        {/* Content */}
        <p
          className={cc([
            "mb-4 flex-grow text-base font-medium leading-relaxed text-zinc-700",
            "line-clamp-3 transition-colors group-hover:text-zinc-900",
          ])}
        >
          {content}
        </p>

        {/* Read more indicator */}
        <div className="mt-auto flex items-center gap-2 text-sm font-medium text-kkm-darkgreen">
          <span className="transition-transform duration-300 group-hover:translate-x-1">
            {readArticleText}
          </span>
          <motion.svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            initial={{ x: 0 }}
            whileHover={{ x: 4 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </motion.svg>
        </div>
      </div>
    </div>
  </motion.a>
)

const HeroSection = ({ title, description }: HeroSectionProps) => {
  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-kkm-green/10 via-white to-white pb-8 pt-32">
      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <ScrollAnimate delay={0}>
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="mb-4 text-4xl font-bold tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl">
              {title}
            </h1>
            <p className="text-lg leading-relaxed text-zinc-600">
              {description}
            </p>
          </div>
        </ScrollAnimate>
      </div>
    </div>
  )
}

const MediaPage = ({
  data: { allPrismicNewsArticle },
}: PageProps<MediaPageData, MediaPageContext>) => {
  const {
    pageMediaTitle,
    mediaPageDescription,
    mediaReadArticle,
    mediaNoArticles,
    mediaCheckBackSoon,
  } = useTranslations()

  const articles = formatArticles(allPrismicNewsArticle) as Article[]

  return (
    <>
      <SEO title={pageMediaTitle} />

      <HeroSection title={pageMediaTitle} description={mediaPageDescription} />

      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
        {articles.length > 0 && (
          <section className="mb-16">
            <motion.div
              className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {articles.map((article, index) => (
                <ScrollAnimate
                  key={article.id}
                  delay={0.05 * (index % 6)}
                  className="h-full"
                >
                  <MediaCard {...article} readArticleText={mediaReadArticle} />
                </ScrollAnimate>
              ))}
            </motion.div>
          </section>
        )}

        {/* Empty state */}
        {articles.length === 0 && (
          <EmptyState
            title={mediaNoArticles}
            description={mediaCheckBackSoon}
          />
        )}
      </div>
    </>
  )
}

export const pageQuery = graphql`
  query ($locale: String!) {
    allPrismicNewsArticle(
      sort: { data: { published_at: DESC } }
      filter: { lang: { eq: $locale } }
    ) {
      edges {
        node {
          id
          data {
            article {
              text
            }
            published_at(formatString: "DD.MM.YYYY")
            link_to_article {
              url
            }
            publisher_logo {
              url
            }
            publisher_name {
              text
            }
          }
        }
      }
    }
  }
`

export default MediaPage
